import { useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, AlertTriangle } from 'lucide-react';
import { useMedidaSets, useCreateMedidaSet } from '../../hooks/useMedidaSets';
import { Button } from '../../components/ui/Button';

/**
 * Auto-creates a new named medida set (Registro de Medidas N) for the given
 * project and redirects immediately to the medidas form. No modal.
 * Used by the "Registrar Medidas" CTA in the project Resumen tab.
 */
export function CreateMedidaSetAutoPage() {
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: sets, isLoading } = useMedidaSets(projectId);
  const createSet = useCreateMedidaSet();
  const fired = useRef(false);

  useEffect(() => {
    if (!projectId || isLoading || fired.current) return;
    fired.current = true;

    const existingSets = sets ?? [];
    let numero = existingSets.length + 1;
    while (existingSets.some((s) => s.nombre === `Registro de Medidas ${numero}`)) {
      numero++;
    }
    const nombre = `Registro de Medidas ${numero}`;

    createSet
      .mutateAsync({ projectId, nombre, silent: true })
      .then((created) => {
        navigate(`/proyectos/${projectId}/medidas?setId=${created.id}`, { replace: true });
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, sets, isLoading]);

  if (createSet.isError) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4">
        <AlertTriangle className="h-10 w-10 text-red-400" />
        <p className="text-sm text-gray-600">No se pudo crear el registro de medidas.</p>
        <Button variant="secondary" onClick={() => navigate(`/proyectos/${projectId}`)}>
          Volver al proyecto
        </Button>
      </div>
    );
  }

  return (
    <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3">
      <Loader2 className="h-8 w-8 animate-spin text-brand-blue" />
      <p className="text-sm text-gray-500">Creando registro de medidas...</p>
    </div>
  );
}
